import type { TFunction } from '~/core/i18n'
import type { RangePosition } from '~/entities/epg/programs'
import type {
  EpgSidebarInfo,
  EpgSidebarLocalizedInfo,
  EpgSidebarProgramInfo,
  EpgSidebarProgramLocalizedInfo,
} from './index.h'

import {
  Day,
  formatEpgProgramRangeLabel,
  formatMidnightCrossingEpgRange,
  rangePosition,
  State,
} from '~/entities/epg/programs'
import {
  formatDateDistanceToNow,
  formatDateDuration,
  formatTimeRangeUnified,
  isOvernightMidnightCrossing,
  timeWithin,
} from '~/shared/helpers/date'

const formatTodayFutureProgramLabel = ({
  untilMs,
}: Readonly<{
  untilMs: number
}>): string => {
  const { hours, minutes } = timeWithin(untilMs)
  if (!hours && !minutes) return ''
  return formatDateDistanceToNow({ minutes, hours })
}

const formatLiveRangeLabel = ({
  t,
  actualStartMs,
  actualStopMs,
}: Readonly<{
  t: TFunction
  actualStartMs: number
  actualStopMs: number
}>): string => {
  // Live keeps time-only range, except midnight crossing needs day labels on both ends
  if (isOvernightMidnightCrossing(actualStartMs, actualStopMs)) {
    return formatMidnightCrossingEpgRange(actualStartMs, actualStopMs, t)
  }
  return formatTimeRangeUnified(actualStartMs, actualStopMs)
}

const formatStartEndSidebar = ({
  t,
  actualStartMs,
  actualStopMs,
  leftMs,
  untilMs,
  position,
}: Readonly<{
  t: TFunction
  actualStartMs: number
  actualStopMs: number
  leftMs: number
  untilMs: number
  position: RangePosition
}>): string[] => {
  // for live program format labels as [live, start-end, left time]
  if (position.state === State.Ongoing) {
    const { hours, minutes } = timeWithin(leftMs)
    const formattedDuration = formatDateDuration({
      duration: { hours, minutes },
      short: true,
    })

    const labelLeft = t('time_left', {
      time: formattedDuration,
    })
    const live = t('live')
    return [
      live,
      formatLiveRangeLabel({ t, actualStartMs, actualStopMs }),
      labelLeft,
    ]
  }

  const rangeLabel = formatEpgProgramRangeLabel(actualStartMs, actualStopMs, t)

  // for today future program format labels as [today + start-end, until time]
  if (position.state === State.Upcoming && position.starts === Day.Today) {
    const labelUntil = formatTodayFutureProgramLabel({ untilMs })
    return [rangeLabel, labelUntil]
  }

  // tomorrow / past / future: single comma-joined label (avoids LabelGroup Dot)
  return [rangeLabel]
}

export const formatProgramToLocalizedView =
  ({ t, now }: { t: TFunction; now: number }) =>
  (info: EpgSidebarProgramInfo): EpgSidebarProgramLocalizedInfo => {
    const position = rangePosition([info.actualStartMs, info.actualStopMs], now)
    return {
      ...info,
      isLive: position.state === State.Ongoing,
      labels: formatStartEndSidebar({
        t,
        actualStartMs: info.actualStartMs,
        actualStopMs: info.actualStopMs,
        leftMs: info.actualStopMs - now,
        untilMs: info.actualStartMs - now,
        position,
      }),
    }
  }

export const formatToLocalizedView =
  ({ t }: { t: TFunction }) =>
  (info: EpgSidebarInfo): EpgSidebarLocalizedInfo => ({
    ...info,
    titleDirectors: t('directors'),
    titleStars: t('stars'),
    titleUpcoming: t('upcoming'),
  })
